import React from 'react';
import { classNames } from 'primereact/utils';
import { InputText } from 'primereact/inputtext';
import { InputMask } from 'primereact/inputmask';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Fieldset } from 'primereact/fieldset';
import { useTranslation } from 'react-i18next';
import { PhoneInput } from '../../PhoneInput';

/**
 * Tab de Dados Basicos do Dojo (Gym).
 * Inclui: nome/tradeName/jkaId/estado/filiado, contatos+redes sociais, endereco.
 *
 * Props:
 *  - gym: objeto atual
 *  - onInputChange(e, name): callback pra alterar campos (aceita dot-notation)
 *  - submitted: bool
 *  - setStreetNumber(value): callback pro InputNumber do numero do endereco
 *  - states: lista de estados
 *  - selectedState, onStateChange
 *  - checked, setChecked: estado do checkbox "Filiado"
 */
export function TabGymBasicData({
    gym,
    onInputChange,
    submitted,
    setStreetNumber,
    states,
    selectedState,
    onStateChange,
    checked,
    setChecked,
}) {
    const { t } = useTranslation();

    return (
        <>
            {/* Bloco 1: Nome, JKA ID, Estado, Filiado */}
            <div className="field">
                <Fieldset>
                    <div className="formgrid grid">
                        <div className="field input text col-10">
                            <label htmlFor="name">{t('msg.jkaName')}</label>
                            <InputText id="name" value={gym.name} onChange={(e) => onInputChange(e, 'name')}
                                required autoFocus
                                className={classNames({ 'p-invalid': submitted && !gym.name })} />
                            {submitted && !gym.name && <small className="p-error">Name is required.</small>}
                        </div>

                        <div className="field input text col-10">
                            <label htmlFor="tradeName">{t('msg.jkaTradeName')}</label>
                            <InputText id="tradeName" value={gym.tradeName}
                                onChange={(e) => onInputChange(e, 'tradeName')}
                                className={classNames({ 'p-invalid': submitted && !gym.tradeName })} />
                            {submitted && !gym.tradeName && <small className="p-error">Trade name is required.</small>}
                        </div>

                        <div className="field dropdown col-3">
                            <label htmlFor="jkaState">{t('msg.jkaState')}</label>
                            <Dropdown value={selectedState} options={states} onChange={onStateChange} optionLabel="name" />
                        </div>

                        <div className="field input text col-3">
                            <label htmlFor="jkaId">{t('msg.jkaIdBR')}</label>
                            <InputText id="jkaId" value={gym.jkaId} onChange={(e) => onInputChange(e, 'jkaId')}
                                className={classNames({ 'p-invalid': submitted && !gym.jkaId })} />
                            {submitted && !gym.jkaId && <small className="p-error">JKA ID is required.</small>}
                        </div>

                        <div className="field input text col-1">
                            <label htmlFor="affiliate">Filiado</label>
                            <Checkbox onChange={(e) => setChecked(e.checked)} checked={checked} />
                        </div>
                    </div>
                </Fieldset>
            </div>

            {/* Bloco 2: Contatos + Redes Sociais */}
            <div className="field">
                <Fieldset>
                    <div className="formgrid grid contacts">
                        <div className="field input text col-3">
                            <label htmlFor="phone1">Telefone 1</label>
                            <PhoneInput id="contacts.phone1" mask="(99)9999-9999"
                                value={gym.contacts.phone1} placeholder="(99)9999-9999"
                                onChange={(e) => onInputChange(e, 'contacts.phone1')} />
                        </div>
                        <div className="field input text col-3">
                            <label htmlFor="phone2">Telefone 2</label>
                            <PhoneInput id="contacts.phone2" mask="(99)9999-9999"
                                value={gym.contacts.phone2} placeholder="(99)9999-9999"
                                onChange={(e) => onInputChange(e, 'contacts.phone2')} />
                        </div>
                        <div className="field input text col-3">
                            <label htmlFor="mobile1">Celular</label>
                            <PhoneInput id="contacts.mobile1" mask="(99)99999-9999"
                                value={gym.contacts.mobile1} placeholder="(99)99999-9999"
                                onChange={(e) => onInputChange(e, 'contacts.mobile1')} />
                        </div>
                        <div className="field input text col-3">
                            <label htmlFor="mobile2">Celular</label>
                            <PhoneInput id="contacts.mobile2" mask="(99)99999-9999"
                                value={gym.contacts.mobile2} placeholder="(99)99999-9999"
                                onChange={(e) => onInputChange(e, 'contacts.mobile2')} />
                        </div>
                        <div className="field input text col-3">
                            <label htmlFor="email1">{t('msg.jkaEmail')}</label>
                            <InputText id="contacts.email1" value={gym.contacts.email1}
                                onChange={(e) => onInputChange(e, 'contacts.email1')} />
                        </div>
                        <div className="field input text col-3">
                            <label htmlFor="email2">{t('msg.jkaEmail')} 2</label>
                            <InputText id="contacts.email2" value={gym.contacts.email2}
                                onChange={(e) => onInputChange(e, 'contacts.email2')} />
                        </div>
                        <div className="field input text col-3">
                            <label htmlFor="email3">{t('msg.jkaEmail')} 3</label>
                            <InputText id="contacts.email3" value={gym.contacts.email3}
                                onChange={(e) => onInputChange(e, 'contacts.email3')} />
                        </div>
                        <div className="field input text col-3">
                            <label htmlFor="instagram">Instagram</label>
                            <InputText id="socialMedia.instagramId" value={gym.socialMedia.instagramId}
                                onChange={(e) => onInputChange(e, 'socialMedia.instagramId')} />
                        </div>
                        <div className="field input text col-3">
                            <label htmlFor="facebook">Facebook</label>
                            <InputText id="socialMedia.facebookId" value={gym.socialMedia.facebookId}
                                onChange={(e) => onInputChange(e, 'socialMedia.facebookId')} />
                        </div>
                        <div className="field input text col-3">
                            <label htmlFor="twitter">Twitter</label>
                            <InputText id="socialMedia.twitterId" value={gym.socialMedia.twitterId}
                                onChange={(e) => onInputChange(e, 'socialMedia.twitterId')} />
                        </div>
                        <div className="field input text col-3">
                            <label htmlFor="tiktok">TikTok</label>
                            <InputText id="socialMedia.tiktokId" value={gym.socialMedia.tiktokId}
                                onChange={(e) => onInputChange(e, 'socialMedia.tiktokId')} />
                        </div>
                        <div className="field input text col-3">
                            <label htmlFor="site">Site</label>
                            <InputText id="socialMedia.site" value={gym.socialMedia.site}
                                onChange={(e) => onInputChange(e, 'socialMedia.site')} />
                        </div>
                    </div>
                </Fieldset>
            </div>

            {/* Bloco 3: Endereco */}
            <div className="field">
                <Fieldset>
                    {t('msg.jkaAddress')}<p></p>
                    <div className="formgrid grid address">
                        <div className="field input text col-6">
                            <label htmlFor="street">Logradouro</label>
                            <InputText id="gymAddress.street" value={gym.gymAddress.street}
                                onChange={(e) => onInputChange(e, 'gymAddress.street')} />
                        </div>
                        <div className="field input text col-2">
                            <label htmlFor="streetNumber">Numero</label>
                            <InputNumber id="gymAddress.streetNumber" value={gym.gymAddress.streetNumber}
                                onValueChange={(e) => setStreetNumber(e.value)} />
                        </div>
                        <div className="field input text col-4">
                            <label htmlFor="complement">Complemento</label>
                            <InputText id="gymAddress.addressComplement"
                                value={gym.gymAddress.addressComplement}
                                onChange={(e) => onInputChange(e, 'gymAddress.addressComplement')} />
                        </div>
                        <div className="field input text col-4">
                            <label htmlFor="neighborhood">Bairro</label>
                            <InputText id="gymAddress.neighborhood" value={gym.gymAddress.neighborhood}
                                onChange={(e) => onInputChange(e, 'gymAddress.neighborhood')} />
                        </div>
                        <div className="field input text col-3">
                            <label htmlFor="zipCode">CEP</label>
                            <InputText id="gymAddress.zipCode" value={gym.gymAddress.zipCode}
                                onChange={(e) => onInputChange(e, 'gymAddress.zipCode')} />
                        </div>
                        <div className="field input text col-3">
                            <label htmlFor="city">Cidade</label>
                            <InputText id="gymAddress.city" value={gym.gymAddress.city}
                                onChange={(e) => onInputChange(e, 'gymAddress.city')} />
                        </div>
                        <div className="field input text col-1">
                            <label htmlFor="state">Estado</label>
                            <InputText id="gymAddress.state" value={gym.gymAddress.state}
                                onChange={(e) => onInputChange(e, 'gymAddress.state')} />
                        </div>
                    </div>
                </Fieldset>
            </div>
        </>
    );
}
